import CustomTooltip from '../../../shared/CustomTooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
    Box,
    Button,
    Divider,
    Grid,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import Collapse from '@mui/material/Collapse';
import React, { useState } from 'react';
import { TransitionGroup } from 'react-transition-group';
import {
    camelCaseToLowerCase,
    capitalizeName,
    formatCurrency,
    groupCleaningFees,
} from '../../../../shared/utils';
import { getCleaningFeeText } from '../../Experiences/Owner/Pricing/CleaningFeeCalendarView';

const CalculateChargesContent = ({
    fees,
    moduleType,
    moduleId,
    isWaitingList,
    spaces,
    isExperiencePage,
    cleaningFee,
    hideFooterTotal,
}: {
    fees: any;
    moduleType: any;
    moduleId: any;
    isWaitingList?: boolean;
    spaces: any[];
    isExperiencePage?: boolean;
    cleaningFee?: any;
    hideFooterTotal?: boolean;
}) => {
    const [showCleaningFeeDetails, setShowCleaningFeeDetails] = useState(false);
    const theme = useTheme();

    const showFromIndicators =
        !!isExperiencePage &&
        !!cleaningFee &&
        cleaningFee?.cleaningFeeType !== 'flat';

    const cleaningFeeDescription = ['perRoom', 'tiered'].includes(
        cleaningFee?.cleaningFeeType,
    )
        ? 'Cleaning fee is subject to the number of rooms or number of guests'
        : 'Cleaning fee is subject to the spaces selected for the reservation';

    const getCleaningDetailKey = (detail: any, fallbackIndex: number) => {
        const zoneId = detail?.feeMultipliers?.[0]?.zoneId;
        if (zoneId !== undefined && zoneId !== null) {
            return `zone:${zoneId}:${detail.type}`;
        }
        const multiplierSig = Array.isArray(detail?.feeMultipliers)
            ? detail.feeMultipliers
                  .map((m: any) => `${m.type}:${m.value}`)
                  .join('|')
            : '';
        return `${detail.chargeType || 'cleaningFee'}:${detail.type}:${
            multiplierSig || fallbackIndex
        }`;
    };

    const getFeeMultiplierText = (feeMultiplier: any, moduleType: any) => {
        return feeMultiplier.value > 1
            ? ['night', 'day'].includes(feeMultiplier.type) &&
              (moduleType === 'commercial' ||
                  moduleType === 'activity' ||
                  moduleType === 'vehicle')
                ? 'days'
                : feeMultiplier.type === 'night'
                  ? 'nights '
                  : feeMultiplier.type === 'person'
                    ? 'guests'
                    : feeMultiplier.type === 'hour'
                      ? 'hours'
                      : feeMultiplier.type
            : feeMultiplier.value === 1 && feeMultiplier.type === 'person'
              ? 'guest'
              : feeMultiplier.value === 1 &&
                  feeMultiplier.type === 'night' &&
                  (moduleType === 'commercial' ||
                      moduleType === 'activity' ||
                      moduleType === 'vehicle')
                ? 'day'
                : camelCaseToLowerCase(feeMultiplier.type);
    };

    const modifiedCharges = groupCleaningFees(fees?.charges ?? []);

    return (
        <Grid
            item
            xs={12}
            sm={12}
            md={12}
            sx={{
                width: '100%',
                minWidth: { xs: 'auto', sm: 'auto', md: 150 },
            }}
        >
            <TransitionGroup>
                {modifiedCharges?.map(
                    (charge: any, index: number, array: any[]) => (
                        <Collapse
                            key={index}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                mb: index !== array.length - 1 ? '12px' : 0,
                            }}
                        >
                            <Stack
                                direction='row'
                                justifyContent='space-between'
                                sx={{
                                    width: '100%',
                                    gap: { xs: 0.5, sm: 1 },
                                    alignItems: 'flex-start',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                    }}
                                >
                                    <Typography
                                        variant='caption'
                                        color='text.primary'
                                        fontWeight={550}
                                        sx={{
                                            minWidth: 0,
                                            flex: '1 1 auto',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            maxWidth: '100%',
                                            fontSize: {
                                                xs: '12px',
                                                sm: '14px',
                                            },
                                            lineHeight: 1.3,
                                        }}
                                        title={
                                            charge?.chargeType === 'cleaningFee'
                                                ? capitalizeName(charge?.name)
                                                : `$${charge?.unitPrice?.toFixed(
                                                      2,
                                                  )} AUD ${charge.feeMultipliers
                                                      .map(
                                                          (m: any) =>
                                                              `x ${
                                                                  m.value
                                                              } ${getFeeMultiplierText(
                                                                  m,
                                                                  moduleType,
                                                              )}`,
                                                      )
                                                      .join(' ')}${
                                                      charge?.isWeekendPrice
                                                          ? ' (Weekend Price)'
                                                          : ''
                                                  }${
                                                      charge?.name
                                                          ? ` (${capitalizeName(
                                                                charge.name,
                                                            )})`
                                                          : ''
                                                  }`
                                        }
                                    >
                                        {charge.chargeType === 'cleaningFee' ? (
                                            <>
                                                {capitalizeName(charge.name)}

                                                {charge.cleaningFeeType !==
                                                    'flat' && (
                                                    <Button
                                                        size='small'
                                                        onClick={() =>
                                                            setShowCleaningFeeDetails(
                                                                (prev) => !prev,
                                                            )
                                                        }
                                                        sx={{
                                                            textTransform:
                                                                'none',
                                                            marginLeft: '8px',
                                                            padding: 0,
                                                            minWidth: 'auto',
                                                            fontSize: {
                                                                xs: '11px',
                                                                sm: '12px',
                                                            },
                                                        }}
                                                    >
                                                        {showCleaningFeeDetails ? (
                                                            <KeyboardArrowUpIcon fontSize='small' />
                                                        ) : (
                                                            <KeyboardArrowDownIcon fontSize='small' />
                                                        )}
                                                    </Button>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                {`$${charge?.unitPrice?.toFixed(
                                                    2,
                                                )} AUD `}
                                                {charge.feeMultipliers.map(
                                                    (feeMultiplier: any) => (
                                                        <Box
                                                            key={`${feeMultiplier.type}-${feeMultiplier.value}`}
                                                            component='span'
                                                        >
                                                            {` x ${
                                                                feeMultiplier.value
                                                            } ${getFeeMultiplierText(
                                                                feeMultiplier,
                                                                moduleType,
                                                            )}`}
                                                        </Box>
                                                    ),
                                                )}

                                                {charge?.isWeekendPrice && (
                                                    <Typography
                                                        fontWeight={550}
                                                        component='span'
                                                        sx={{
                                                            fontSize: '14px',
                                                            marginTop: '5px',
                                                        }}
                                                    >
                                                        {' '}
                                                        (Weekend Price)
                                                    </Typography>
                                                )}

                                                {charge?.name
                                                    ? ` (${capitalizeName(
                                                          charge.name,
                                                      )})`
                                                    : ``}
                                            </>
                                        )}
                                    </Typography>

                                    {charge.priceOverridden && (
                                        <CustomTooltip
                                            title={
                                                <Typography>
                                                    This experience has seasonal
                                                    rate changes for the
                                                    selected period
                                                </Typography>
                                            }
                                            placement='right'
                                        >
                                            <HelpOutlineIcon
                                                sx={{
                                                    color: 'text.primary',
                                                    fontSize: '16px',
                                                }}
                                            />
                                        </CustomTooltip>
                                    )}
                                </Box>

                                <Typography
                                    color='text.primary'
                                    variant='caption'
                                    fontWeight={550}
                                    sx={{
                                        flex: '0 0 auto',
                                        alignSelf: 'flex-start',
                                        fontSize: { xs: '12px', sm: '14px' },
                                        lineHeight: 1.3,
                                        pl: 1,
                                        textAlign: 'flex-start',
                                    }}
                                >
                                    {showFromIndicators &&
                                        charge.chargeType === 'cleaningFee' && (
                                            <>
                                                <span
                                                    style={{
                                                        fontSize: '12px',
                                                    }}
                                                >
                                                    From
                                                </span>
                                                <span
                                                    style={{
                                                        fontSize: '11px',
                                                        paddingRight: '5px',
                                                    }}
                                                >
                                                    *
                                                </span>
                                            </>
                                        )}
                                    {formatCurrency(charge?.total)}
                                </Typography>
                            </Stack>

                            {charge.chargeType === 'cleaningFee' && (
                                <>
                                    <Collapse
                                        in={
                                            showCleaningFeeDetails &&
                                            charge.cleaningFeeType !== 'flat'
                                        }
                                    >
                                        <Stack
                                            sx={{
                                                width: '100%',
                                                marginTop: '6px',
                                                gap: 0.5,
                                                paddingLeft: '8px',
                                            }}
                                        >
                                            <TransitionGroup>
                                                {charge?.details
                                                    ?.filter(
                                                        (detail: any) =>
                                                            detail.total > 0,
                                                    )
                                                    .map(
                                                        (
                                                            detail: any,
                                                            detailIndex: number,
                                                        ) => (
                                                            <Collapse
                                                                key={getCleaningDetailKey(
                                                                    detail,
                                                                    detailIndex,
                                                                )}
                                                            >
                                                                <Grid
                                                                    sx={{
                                                                        display:
                                                                            'flex',
                                                                        justifyContent:
                                                                            'space-between',
                                                                        width: '100%',
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        color='text.primary'
                                                                        fontWeight={
                                                                            550
                                                                        }
                                                                        variant='caption'
                                                                        sx={{
                                                                            fontStyle:
                                                                                'italic',
                                                                        }}
                                                                    >
                                                                        {
                                                                            getCleaningFeeText(
                                                                                detail,
                                                                                spaces,
                                                                            )
                                                                                .description
                                                                        }
                                                                    </Typography>
                                                                    <Typography
                                                                        color='text.primary'
                                                                        variant='caption'
                                                                        fontWeight={
                                                                            550
                                                                        }
                                                                        sx={{
                                                                            fontStyle:
                                                                                'italic',
                                                                        }}
                                                                    >
                                                                        {
                                                                            getCleaningFeeText(
                                                                                detail,
                                                                                spaces,
                                                                            )
                                                                                .amount
                                                                        }
                                                                    </Typography>
                                                                </Grid>
                                                            </Collapse>
                                                        ),
                                                    )}
                                            </TransitionGroup>
                                        </Stack>
                                    </Collapse>
                                </>
                            )}
                        </Collapse>
                    ),
                )}
            </TransitionGroup>

            {moduleId !== 9 && (
                <Collapse in={!!fees?.charges}>
                    <Divider
                        sx={{
                            mt: '20px',
                            mb: '20px',
                            color: theme.palette.faded.main,
                        }}
                    />
                    <Stack sx={{ gap: 1 }}>
                        <Stack
                            direction='row'
                            justifyContent='space-between'
                            alignItems='center'
                            flexWrap='nowrap'
                            aria-labelledby='subtotal-label'
                            sx={{ width: '100%' }}
                        >
                            <Typography
                                id='subtotal-label'
                                variant='caption'
                                color='text.primary'
                                fontWeight={550}
                                noWrap
                                sx={{
                                    minWidth: 0,
                                    flex: '1 1 auto',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    fontSize: { xs: '12px', sm: '14px' },
                                }}
                            >
                                Subtotal
                            </Typography>
                            <Typography
                                variant='caption'
                                color='text.primary'
                                fontWeight={550}
                                noWrap
                                sx={{
                                    flex: '0 0 auto',
                                    fontSize: { xs: '12px', sm: '14px' },
                                    pl: 1,
                                }}
                            >
                                {formatCurrency(fees?.bookingTotal?.subTotal)}
                            </Typography>
                        </Stack>

                        {fees?.bookingTotal?.discount > 0 && (
                            <Stack
                                direction='row'
                                justifyContent='space-between'
                                alignItems='center'
                                flexWrap='nowrap'
                                sx={{ width: '100%' }}
                            >
                                <Typography
                                    variant='caption'
                                    color='text.primary'
                                    fontWeight={550}
                                    noWrap
                                    sx={{
                                        minWidth: 0,
                                        flex: '1 1 auto',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        fontSize: { xs: '12px', sm: '14px' },
                                    }}
                                >
                                    Discount
                                </Typography>
                                <Typography
                                    variant='caption'
                                    color='text.primary'
                                    fontWeight={550}
                                    noWrap
                                    sx={{
                                        flex: '0 0 auto',
                                        fontSize: { xs: '12px', sm: '14px' },
                                        pl: 1,
                                    }}
                                >
                                    -{' '}
                                    {formatCurrency(
                                        fees?.bookingTotal?.discount,
                                    )}
                                </Typography>
                            </Stack>
                        )}

                        {!!fees?.bookingTotal?.guestServiceFee && (
                            <Stack
                                direction='row'
                                justifyContent='space-between'
                                alignItems='center'
                                flexWrap='nowrap'
                                aria-labelledby='guest-service-fee'
                                sx={{ width: '100%' }}
                            >
                                <Typography
                                    variant='caption'
                                    color='text.primary'
                                    id='guest-service-fee'
                                    fontWeight={550}
                                    noWrap
                                    sx={{
                                        minWidth: 0,
                                        flex: '1 1 auto',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        fontSize: { xs: '12px', sm: '14px' },
                                    }}
                                >
                                    7DAY Service Fee{' '}
                                    {
                                        fees?.bookingTotal
                                            .guestServiceFeePercentage
                                    }
                                    %
                                </Typography>
                                <Typography
                                    variant='caption'
                                    color='text.primary'
                                    fontWeight={550}
                                    noWrap
                                    sx={{
                                        flex: '0 0 auto',
                                        fontSize: { xs: '12px', sm: '14px' },
                                        pl: 1,
                                    }}
                                >
                                    {formatCurrency(
                                        fees?.bookingTotal.guestServiceFee,
                                    )}
                                </Typography>
                            </Stack>
                        )}

                        <Stack
                            direction='row'
                            justifyContent='space-between'
                            alignItems='center'
                            flexWrap='nowrap'
                            sx={{ width: '100%' }}
                            aria-labelledby='gst'
                        >
                            <Typography
                                variant='caption'
                                id='gst'
                                color='text.primary'
                                fontWeight={550}
                                noWrap
                                sx={{
                                    minWidth: 0,
                                    flex: '1 1 auto',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    fontSize: { xs: '12px', sm: '14px' },
                                }}
                            >
                                GST 10%
                            </Typography>
                            <Typography
                                variant='caption'
                                color='text.primary'
                                fontWeight={550}
                                noWrap
                                sx={{
                                    flex: '0 0 auto',
                                    fontSize: { xs: '12px', sm: '14px' },
                                    pl: 1,
                                }}
                            >
                                {formatCurrency(fees?.bookingTotal?.gst)}
                            </Typography>
                        </Stack>

                        {!hideFooterTotal && (
                            <Stack
                                direction='row'
                                justifyContent='space-between'
                                alignItems='center'
                                flexWrap='nowrap'
                                sx={{ width: '100%' }}
                            >
                                <Typography
                                    variant='caption'
                                    color='text.primary'
                                    noWrap
                                    sx={{
                                        minWidth: 0,
                                        flex: '1 1 auto',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        fontWeight: 'bold',
                                        fontSize: { xs: '12px', sm: '14px' },
                                    }}
                                >
                                    {`Total ${isWaitingList ? '*' : ''}`}
                                </Typography>
                                <Typography
                                    variant='caption'
                                    color='text.primary'
                                    noWrap
                                    sx={{
                                        flex: '0 0 auto',
                                        fontWeight: 'bold',
                                        fontSize: { xs: '12px', sm: '14px' },
                                        pl: 1,
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: '12px',
                                            paddingRight: '5px',
                                        }}
                                    >
                                        {showFromIndicators ? 'From' : ''}
                                    </span>
                                    {formatCurrency(fees?.bookingTotal?.total)}
                                </Typography>
                            </Stack>
                        )}

                        {showFromIndicators && (
                            <Typography
                                variant='caption'
                                color='text.primary'
                                sx={{ mt: 0.5 }}
                            >
                                {`*${cleaningFeeDescription}`}
                            </Typography>
                        )}

                        {isWaitingList && (
                            <Stack
                                direction='row'
                                alignItems='center'
                                justifyContent='flex-start'
                                flexWrap='nowrap'
                                sx={{ mt: '12px', width: '100%' }}
                                gap={0.5}
                            >
                                <Typography
                                    variant='caption'
                                    color='text.primary'
                                    noWrap
                                    sx={{
                                        minWidth: 0,
                                        flex: '1 1 auto',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        fontSize: '12px',
                                        fontStyle: 'italic',
                                    }}
                                >
                                    * Estimated total for reference only
                                </Typography>
                                <CustomTooltip
                                    title={
                                        <Typography
                                            variant='caption'
                                            color='text.primary'
                                            sx={{
                                                fontSize: '12px',
                                            }}
                                        >
                                            The final price will be calculated
                                            at the time of reservation, based on
                                            the current rates, fees, taxes, and
                                            any applicable discounts.
                                        </Typography>
                                    }
                                    placement='right'
                                >
                                    <HelpOutlineIcon
                                        sx={{
                                            fontSize: 16,
                                            cursor: 'pointer',
                                            flex: '0 0 auto',
                                        }}
                                    />
                                </CustomTooltip>
                            </Stack>
                        )}
                    </Stack>
                </Collapse>
            )}
        </Grid>
    );
};

export default CalculateChargesContent;
